import { FIELD_TYPES } from "@salestrak/custom-fields";
import { z } from "zod";

import {
  FORM_CONDITION_OPS,
  FORM_PROPERTIES,
  FORM_VISIBILITIES,
  MAX_SUBMISSION_LIMIT,
  MAX_SUBMIT_LABEL_LENGTH,
  type FormSettings,
  type FormShape,
} from "./types.ts";

export const FORM_LIMITS = {
  items: 300,
  label: 500,
  body: 20_000,
  options: 200,
  optionLabel: 200,
  ids: 500,
  name: 200,
  description: 5_000,
  answersBytes: 256_000,
} as const;

const id = z.string().min(1).max(64);
const idList = z.array(id).max(FORM_LIMITS.ids);
const fieldType = z.enum(Object.values(FIELD_TYPES) as [string, ...string[]]);

const option = z.object({
  label: z.string().max(FORM_LIMITS.optionLabel).nullish(),
  value: z.string().max(FORM_LIMITS.optionLabel).nullish(),
  color: z.string().max(32).nullish(),
});

const number = z.number().finite().nullish();

// Anything else a client sends (`__typename`, a cache id) is stripped before storing.
const fieldConfig = z.object({
  allowOptionCreate: z.boolean().nullish(),
  currency: z.string().max(8).nullish(),
  decimalPlaces: number,
  defaultNow: z.boolean().nullish(),
  defaultValue: z.unknown().optional(),
  includeTeams: z.boolean().nullish(),
  includeTime: z.boolean().nullish(),
  max: number,
  maxDate: z.string().max(40).nullish(),
  min: number,
  minDate: z.string().max(40).nullish(),
  multiline: z.boolean().nullish(),
  multiple: z.boolean().nullish(),
  optionSort: z.string().max(32).nullish(),
  options: z.array(option.nullable()).max(FORM_LIMITS.options).nullish(),
  showOptionColors: z.boolean().nullish(),
  step: number,
  thousandsSeparator: z.boolean().nullish(),
  wholeNumbers: z.boolean().nullish(),
});

const field = z.object({
  customFieldId: z.string().min(1).max(128),
  name: z.string().max(FORM_LIMITS.label),
  type: fieldType,
  config: fieldConfig.nullish(),
});

const condition = z.object({
  whenItemId: id,
  op: z.enum(FORM_CONDITION_OPS),
  values: z.array(z.string().max(FORM_LIMITS.optionLabel)).max(FORM_LIMITS.options).optional(),
});

const base = {
  itemId: id,
  label: z.string().max(FORM_LIMITS.label),
  description: z.string().max(FORM_LIMITS.description).nullish(),
  showWhen: condition.optional(),
};

const linkedRules = {
  required: z.boolean(),
  prefill: z.boolean().optional(),
  requiredChoices: z.array(z.string().max(FORM_LIMITS.optionLabel)).max(FORM_LIMITS.options).optional(),
  defaultChoices: z.array(z.string().max(FORM_LIMITS.optionLabel)).max(FORM_LIMITS.options).optional(),
};

const item = z.discriminatedUnion("kind", [
  z.object({ ...base, ...linkedRules, kind: z.literal("customField"), customFieldId: id, fieldType: fieldType.optional() }),
  z.object({ ...base, ...linkedRules, kind: z.literal("property"), property: z.enum(FORM_PROPERTIES) }),
  z.object({
    ...base,
    kind: z.literal("question"),
    field,
    required: z.boolean(),
    requiredChoices: linkedRules.requiredChoices,
  }),
  z.object({ ...base, kind: z.literal("attachment"), field, required: z.boolean() }),
  // A section is a page break and never conditional; a rule on one drops.
  z.object({ ...base, kind: z.literal("section") }).transform(({ showWhen: _dropped, ...section }) => section),
  z.object({ ...base, kind: z.literal("text"), body: z.string().max(FORM_LIMITS.body) }),
]);

export const formSettingsSchema = z.object({
  visibility: z.enum(FORM_VISIBILITIES).optional(),
  offeredOn: z.object({ workflowIds: idList, issueTypeIds: idList }).optional(),
  submissionLimit: z.number().int().min(1).max(MAX_SUBMISSION_LIMIT).optional(),
  submitLabel: z.string().max(MAX_SUBMIT_LABEL_LENGTH).optional(),
  resubmit: z.boolean().optional(),
});

export const formShapeSchema = z
  .object({
    items: z.array(item).max(FORM_LIMITS.items),
  })
  .refine((shape) => new Set(shape.items.map((row) => row.itemId)).size === shape.items.length, {
    message: "Item ids must be unique",
  });

export const formShareSchema = z.object({ kind: z.enum(["user", "team"]), id });
export const formSharesSchema = z.array(formShareSchema).max(FORM_LIMITS.ids);

export const formAnswersSchema = z
  .record(id, z.unknown())
  .refine((answers) => JSON.stringify(answers).length <= FORM_LIMITS.answersBytes, {
    message: "Answers are too large",
  });

export function parseFormSettings(raw: unknown): { ok: true; settings: FormSettings } | { ok: false; error: string } {
  const result = formSettingsSchema.safeParse(raw);
  if (!result.success) return { ok: false, error: z.prettifyError(result.error) };
  return { ok: true, settings: result.data as FormSettings };
}

export function parseFormShape(raw: unknown): { ok: true; shape: FormShape } | { ok: false; error: string } {
  const result = formShapeSchema.safeParse(raw);
  if (!result.success) return { ok: false, error: z.prettifyError(result.error) };
  return { ok: true, shape: result.data as FormShape };
}
